import { useEffect, useRef } from "react";

const EVENT_TYPES = [
  "mousemove",
  "keydown",
  "mousedown",
  "touchstart",
  "scroll",
];

interface Props {
  debounceTime?: number;
  checkInterval?: number;
  onActivity?: () => void;
  onIntervalActivity?: () => Promise<void>;
  onIntervalNoActivity?: (options: { durationInMs: number }) => void;
}

export function useActivityDetection({
  debounceTime = 5000, // 5 seconds
  checkInterval = 240000, // 4 minutes
  onActivity,
  onIntervalActivity,
  onIntervalNoActivity,
}: Props) {
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const intervalTimer = useRef<NodeJS.Timeout | null>(null);
  const hasActivity = useRef(false);
  const noActivityFor = useRef(0);

  const resetActivity = () => {
    hasActivity.current = true;
    noActivityFor.current = 0;
  };

  const handleActivity = () => {
    hasActivity.current = true;
    noActivityFor.current = 0;
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    if (onActivity) {
      debounceTimer.current = setTimeout(() => {
        if (onActivity) onActivity();
      }, debounceTime);
    }
  };

  const resetActivityDetection = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    if (intervalTimer.current) {
      clearInterval(intervalTimer.current);
    }
    EVENT_TYPES.forEach((event) => {
      window.removeEventListener(event, handleActivity);
    });
    hasActivity.current = false;
    noActivityFor.current = 0;
  };

  useEffect(() => {
    const checkActivity = async () => {
      if (hasActivity.current && onIntervalActivity) {
        await onIntervalActivity();
      } else if (!hasActivity.current && onIntervalNoActivity) {
        noActivityFor.current += checkInterval;
        onIntervalNoActivity({ durationInMs: noActivityFor.current });
      }
      hasActivity.current = false;
    };

    // Set up the interval to check for activity during the specified interval
    intervalTimer.current = setInterval(checkActivity, checkInterval);

    // Listen for activity events
    EVENT_TYPES.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      resetActivityDetection();
    };
  }, [
    debounceTime,
    checkInterval,
    onActivity,
    onIntervalActivity,
    onIntervalNoActivity,
  ]);

  return { resetActivity };
}
